'use client';

import { useEffect, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useRBAC } from '@/contexts/RBACContext';
import { getRequiredPermissionForPath } from '@/lib/routePathPermissions';
import { resolveBetaNavPermission } from '@/lib/betaNavAccess';
import Loader from '@/components/Loader/Loader';

/**
 * After RBAC loads, redirects to the dashboard if the current route requires a permission the user lacks
 * (e.g. after switching sub-tenant in the header, scoped permissions may no longer include this page).
 * Dashboard is intentionally ungated in routePathPermissions. Must render inside RBACProvider.
 */
export default function PermissionRouteGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || '';
  const router = useRouter();
  const rbac = useRBAC();

  const required = useMemo(() => getRequiredPermissionForPath(pathname), [pathname]);

  const hasAccess = useMemo(() => {
    if (!required) return true;
    
    // Beta grants (Command Center, Blyn) need an exact grant — owners don't get them via hasPermission.
    return resolveBetaNavPermission(rbac, required) ?? rbac.hasPermission(required);
  }, [required, rbac]);

  useEffect(() => {
    if (!required || rbac.isLoading || rbac.isSubTenantScopeLoading) return;
    if (!hasAccess) {
      router.replace('/dashboard');
    }
  }, [
    required,
    rbac.isLoading,
    rbac.isSubTenantScopeLoading,
    hasAccess,
    router,
  ]);

  if (!required) {
    return <>{children}</>;
  }

  // Render optimistically while RBAC is still resolving (hasAccess defaults
  // to false until userContext loads, so we can't use it to gate here without
  // also blocking the common "not yet known" case). Only block once loading
  // has actually finished and access is confirmed denied; the redirect effect
  // above fires on the same condition, so a denied user still gets redirected
  // — this can only ever remove a wait, never grant access it shouldn't.
  if (!rbac.isLoading && !rbac.isSubTenantScopeLoading && !hasAccess) {
    return <Loader showDelay />;
  }

  return <>{children}</>;
}
